import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { FooterComponent } from '../layout/footer/footer.component';
import { NavbarComponent } from '../layout/navbar/navbar.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { ClickOutsideDirective } from '../../shared/directives/click-outside.directive';
import {
  TranslationService
} from '../../core/services/translation.service';
import { UiPreferencesService } from '../../core/services/ui-preferences.service';
import { type AppLanguageCode, type AppLanguageOption } from '../../core/models/app-language.model';
import { AdminUsersService } from '../../core/services/admin-users.service';
import { type AdminUser } from '../../core/models/admin-user.model';
import { extractHttpErrorMessage } from '../../core/utils/http-error.util';

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [FormsModule, NavbarComponent, FooterComponent, TranslatePipe, ClickOutsideDirective],
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.css'
})
export class AdminComponent implements OnInit {
  protected readonly languages: readonly AppLanguageOption[];
  protected readonly darkMode;

  protected isLoading = false;
  protected isSaving = false;
  protected deletingUco: string | null = null;
  protected errorMessage = '';
  protected modalOpen = false;
  protected roleMenuOpen = false;
  protected newUco = '';
  protected newRole = 'User';
  protected editingUco: string | null = null;
  protected users: AdminUser[] = [];
  protected readonly roleOptions = ['User', 'Admin'] as const;

  constructor(
    protected readonly translationService: TranslationService,
    private readonly uiPreferences: UiPreferencesService,
    private readonly adminUsersService: AdminUsersService
  ) {
    this.languages = this.translationService.languageOptions;
    this.darkMode = this.uiPreferences.darkMode;
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  protected toggleTheme(): void {
    this.uiPreferences.toggleDarkMode();
  }

  protected async setLanguage(language: AppLanguageCode): Promise<void> {
    await this.translationService.setLanguage(language);
  }

  protected openModal(): void {
    this.editingUco = null;
    this.newUco = '';
    this.newRole = 'User';
    this.roleMenuOpen = false;
    this.modalOpen = true;
  }

  protected openEditModal(user: AdminUser): void {
    this.editingUco = user.uco;
    this.newUco = user.uco;
    this.newRole = user.role;
    this.roleMenuOpen = false;
    this.modalOpen = true;
  }

  protected isEditing(): boolean {
    return this.editingUco !== null;
  }

  protected closeModal(): void {
    if (this.isSaving) {
      return;
    }

    this.modalOpen = false;
    this.editingUco = null;
    this.newUco = '';
    this.newRole = 'User';
    this.roleMenuOpen = false;
  }

  protected toggleRoleMenu(): void {
    this.roleMenuOpen = !this.roleMenuOpen;
  }

  protected closeRoleMenu(): void {
    this.roleMenuOpen = false;
  }

  protected applyRoleOption(role: string): void {
    this.newRole = role;
    this.roleMenuOpen = false;
  }

  protected saveUser(): void {
    const normalizedUco = this.newUco.trim();
    const normalizedRole = this.newRole.trim();

    if (!normalizedUco || !normalizedRole || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    const request = this.isEditing()
      ? this.adminUsersService.updateUserRole({
        uco: normalizedUco,
        role: normalizedRole
      })
      : this.adminUsersService.addUserByUco({
        uco: normalizedUco,
        role: normalizedRole
      });

    request.subscribe({
      next: (savedUser) => {
        if (this.isEditing()) {
          this.users = this.users.map((user) => user.uco === savedUser.uco ? savedUser : user);
        } else {
          this.users = [savedUser, ...this.users];
        }

        this.isSaving = false;
        this.closeModal();
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.extractErrorMessage(
          error,
          this.isEditing() ? 'Unable to update user role.' : 'Unable to add user.'
        );
        this.isSaving = false;
      }
    });
  }

  protected formatUtc(dateValue: string): string {
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return dateValue;
    }

    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(parsedDate);
  }

  protected deleteUser(user: AdminUser): void {
    if (this.deletingUco || !window.confirm(`Delete user ${user.uco}?`)) {
      return;
    }

    this.deletingUco = user.uco;
    this.errorMessage = '';

    this.adminUsersService.deleteUser(user.uco).subscribe({
      next: () => {
        this.users = this.users.filter((item) => item.uco !== user.uco);
        this.deletingUco = null;
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.extractErrorMessage(error, 'Unable to delete user.');
        this.deletingUco = null;
      }
    });
  }

  private loadUsers(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.adminUsersService.getAllUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.extractErrorMessage(error, 'Unable to load users.');
        this.isLoading = false;
      }
    });
  }

  private extractErrorMessage(error: HttpErrorResponse, fallback: string): string {
    return extractHttpErrorMessage(error, fallback);
  }
}
